import { NO_ANSWER_CODES, shownDuration } from '@dbrain/app-core/reply-failure';
import {
  isMicrosoft365TurnCode,
  type ConversationDto,
  type ConversationEvent,
  type ConversationFileDto,
  type ConversationStateDto,
  type DirectoryDto,
  type Microsoft365Source,
} from '@dbrain/shared';
import { TAB_TITLE } from '../tab-title.js';
import { Badge, Button, IconButton, Icons, Menu, MenuItem, MenuSeparator, PageLoading, Switch, TextInput } from '@dbrain/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { api, ApiClientError, upload } from '../api/client.js';
import { docKeys, useConversationDocuments } from '../api/documents.js';
import { keys, useConversation, useMe, useWorkspace } from '../api/hooks.js';
import { useProductOn, useReadOnly } from '../api/license.js';
import { microsoft365Key, useMicrosoft365Status } from '../api/microsoft365.js';
import { addConversationFile } from '../api/microsoft365-files.js';
import { AnswerCardError, AnswerCardView } from '../components/chat/AnswerCards.js';
import { Microsoft365ConversationChip } from '../components/microsoft365/ConversationChip.js';
import { AddMenu, AddMenuRow } from '../components/microsoft365/files/AddMenu.js';
import { FilePicker } from '../components/microsoft365/files/FilePicker.js';
import { ImportNotice, type ImportSummary } from '../components/microsoft365/files/ImportResult.js';
import { addMenuView, PICKER_PARAM, pickerSourceOf, type PickedFile } from '../components/microsoft365/files/model.js';
import { Microsoft365Notice } from '../components/microsoft365/Notice.js';
import { Microsoft365Result } from '../components/microsoft365/Result.js';
import { answerParts, answerPlainText } from '@dbrain/app-core/answer-cards';
import {
  ApprovalCard,
  ApprovedPlan,
  HelperGroup,
  ModeSelector,
  NoticeLine,
  stateKey,
  TasksPanel,
  TodoPanel,
  useCardNotifications,
} from '../components/chat/AgentParts.js';
import { FailureCard } from '../components/chat/FailureCard.js';
import { BudgetNotice } from '../components/chat/BudgetNotice.js';
import { AllowanceCard, allowancePlaceholder, OversizeChip, OversizeNotice, PlanChip, ProBadge } from '../components/plan/PlanParts.js';
import { usePlan, useUploadLimitMb } from '../api/plan.js';
import { CapabilitiesLine, ConversationToolsDialog, SourcesPanel } from '../components/chat/CapabilityParts.js';
import { ArtifactCard, AttachmentChips, ToolCard } from '../components/chat/ChatParts.js';
import { ComposerFiles, useComposerFiles } from '../components/chat/ComposerFiles.js';
import { Steps } from '../components/chat/Steps.js';
import { useFollowBottom } from '../components/chat/useFollowBottom.js';
import { useSlashMenu } from '../components/chat/useSlashMenu.js';
import { useShell } from '../components/shell/context.js';
import { DrawerButton, ExperienceSwitch } from '../components/shell/TopBar.js';
import { Markdown } from '../components/chat/Markdown.js';
import {
  AnswerActions,
  CompactionLine,
  EditMessage,
  ForkedLine,
  MemoryLine,
  ModelMenu,
  ModelPicker,
  RecapPanel,
  RewindDialog,
  SideQuestions,
  SlashHelp,
  SlashMenu,
  UserMessageActions,
  type SideAnswer,
} from '../components/chat/SessionParts.js';
import { CitationPanel, ConversationDocumentsMenu } from '../components/documents/ConversationDocuments.js';
import { ContextRing } from '../components/chat/ContextPopover.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { ErrorBoundary, PanelError } from '../components/ErrorBoundary.js';
import { LicenseChip } from '../components/license/LicenseChip.js';
import { ReadOnlyCard } from '../components/license/ReadOnlyCard.js';
import { useCodePanel } from '../components/sandbox/code-panel.js';
import { PanelDivider, PanelToggle } from '../components/sandbox/PanelControls.js';
import { filesKey, SandboxPanel, useSandboxStatus, type PanelRequest } from '../components/sandbox/SandboxPanel.js';
import { fileResolver, knownFiles } from '@dbrain/app-core/workspace-files';
import { SkillAddDialog } from '../components/skills/SkillAddDialog.js';
import {
  applyEvent,
  canContinue,
  initialChatState,
  lastAnswer,
  pendingApprovals,
  resetDrafts,
  rewindPoints,
  type ChatItem,
  type ChatState,
} from '@dbrain/app-core/conversation-state';
import { blocks, readSources, showsLine, turnSteps, waitsForAllowance, type Block } from '@dbrain/app-core/steps';
import { homeOf, useExperience, useMeta } from '../experience.js';
import { currentLocale } from '../i18n/index.js';
import { parseSlash, skillAddSource, type SlashCommand } from '@dbrain/app-core/slash';

/** until the server says its limit */
const DEFAULT_UPLOAD_MB = 25;
/** after this long away, a recap is offered (QW-212) */
const RECAP_AFTER_MS = 6 * 60 * 60 * 1000;

type Action = { type: 'event'; event: ConversationEvent } | { type: 'reconnected' } | { type: 'reset' };

function reducer(state: ChatState, action: Action): ChatState {
  if (action.type === 'reset') return initialChatState;
  return action.type === 'event' ? applyEvent(state, action.event) : resetDrafts(state);
}

type Connection = 'connecting' | 'open' | 'reconnecting' | 'closed';

/** Subscribes to the conversation's SSE stream; the browser resumes with Last-Event-ID (SA-018, QW-109). */
function useConversationStream(conversationId: string, enabled: boolean) {
  const [state, dispatch] = useReducer(reducer, initialChatState);
  const [connection, setConnection] = useState<Connection>('connecting');
  useEffect(() => {
    if (!enabled) return;
    dispatch({ type: 'reset' });
    const source = new EventSource(`/api/conversations/${conversationId}/events?after=0`);
    source.onopen = () => {
      setConnection('open');
      dispatch({ type: 'reconnected' });
    };
    source.onmessage = (message) => {
      try {
        dispatch({ type: 'event', event: JSON.parse(message.data as string) as ConversationEvent });
      } catch {
        // ignore malformed frames
      }
    };
    source.onerror = () => setConnection(source.readyState === EventSource.CLOSED ? 'closed' : 'reconnecting');
    return () => source.close();
  }, [conversationId, enabled]);
  return { state, connection };
}

type ToolItem = Extract<ChatItem, { kind: 'tool' }>;
type AssistantItem = Extract<ChatItem, { kind: 'assistant' }>;
type Row = { kind: 'item'; item: ChatItem } | { kind: 'helpers'; items: ToolItem[] };

/** Helpers started in one reply form one group (QW-809). */
function rows(items: ChatItem[]): Row[] {
  const out: Row[] = [];
  for (const item of items) {
    if (item.kind === 'tool' && item.tool === 'agent') {
      const last = out.at(-1);
      if (last?.kind === 'helpers' && last.items[0]?.stepId === item.stepId) last.items.push(item);
      else out.push({ kind: 'helpers', items: [item] });
      continue;
    }
    out.push({ kind: 'item', item });
  }
  return out;
}

/** In CompanyCode, changes and commands keep a card of their own; the rest are steps (Phase 13b). */
const CODE_CARDS = new Set(['edit', 'write_file', 'append_file', 'shell', 'shell_jobs', 'run_code']);
/** A message to show: its text, or Microsoft 365 answer cards (D-197). */
const hasText = (item: ChatItem): item is AssistantItem => item.kind === 'assistant' && (item.text.trim().length > 0 || item.cards.length > 0);
/**
 * The turn ends after which connecting the account lets Oppy answer on its own (Phase 15e, MsDurumlar); part 3: also
 * after the upgrade sign-in of "Yeni izin gerekiyor" (the question goes on with the new permission).
 */
const CONNECT_CODES = new Set(['microsoft_not_connected', 'microsoft_reconnect', 'microsoft_scopes_required']);

function openMenu(testId: string): void {
  document.querySelector(`[data-testid="${testId}"]`)?.setAttribute('open', '');
}

export function ConversationPage() {
  const { t } = useTranslation();
  const { conversationId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meta = useMeta();
  // v0.15a.3: an installation on an IP address has no previews; a server's line opens the files instead
  const previewsOff = meta.data?.previews === false;
  // D-152: the home page opened the user's own code workspace for this conversation; said once, not after a reload
  const location = useLocation();
  const arrived = useRef((location.state as { personalWorkspace?: boolean } | null)?.personalWorkspace === true);
  const [personalOpened] = useState(() => (arrived.current ? conversationId : null));
  useEffect(() => {
    if (!arrived.current) return;
    arrived.current = false;
    void navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [navigate, location.pathname, location.search]);
  const experience = useExperience();
  // D-124: past the license's grace the message box gives way to the read-only card
  const readOnly = useReadOnly();
  // D-125: the other product's switch is hidden when the license does not have it
  const otherOn = useProductOn(experience === 'chat' ? 'code' : 'chat');
  const conversation = useConversation(conversationId);
  const { state, connection } = useConversationStream(conversationId, conversation.isSuccess);
  // the connection is mentioned only once it is lost, or slow to come (D-79: no pill flashing on every load)
  const [slowConnect, setSlowConnect] = useState(false);
  useEffect(() => {
    setSlowConnect(false);
    if (connection !== 'connecting') return;
    const timer = setTimeout(() => setSlowConnect(true), 3000);
    return () => clearTimeout(timer);
  }, [connection]);
  const lost = connection === 'reconnecting' || connection === 'closed' || slowConnect;
  const server = useQuery({
    queryKey: stateKey(conversationId),
    queryFn: () => api<ConversationStateDto>('GET', `/api/conversations/${conversationId}/state`),
    enabled: conversation.isSuccess,
  });
  const workspace = useWorkspace(conversation.data?.workspaceId ?? '');
  // Phase 12: the documents the conversation searches, and the citation whose source is open
  const documents = useConversationDocuments(conversationId, conversation.isSuccess);
  const [cited, setCited] = useState<{ messageId: string; n: number } | null>(null);
  // D-99: CompanyCode's panel, shown or hidden and how wide, per user; it opens only on a click that needs it
  const me = useMe();
  const codePanel = useCodePanel(me.data?.user.id ?? '');
  const layout = useRef<HTMLDivElement>(null);
  const [panelRequest, setPanelRequest] = useState<PanelRequest | null>(null);
  const isCode = conversation.data?.profile === 'code';
  const sandbox = useSandboxStatus(conversation.data?.workspaceId ?? '', isCode);
  const sandboxActive = sandbox.data?.state === 'running' || sandbox.data?.state === 'idle';
  // the workspace's top folder, read only while its environment runs (the files tab shares it)
  const topFolder = useQuery({
    queryKey: filesKey(conversation.data?.workspaceId ?? '', '.'),
    queryFn: () => api<DirectoryDto>('GET', `/api/workspaces/${conversation.data?.workspaceId}/files?path=.`),
    enabled: isCode && sandboxActive,
  });
  // the width is a CSS variable of the layout, set through the CSSOM (no style attribute in the markup: CSP)
  const layoutReady = isCode && Boolean(workspace.data);
  useLayoutEffect(() => {
    const element = layout.current;
    if (!element) return;
    if (codePanel.width) element.style.setProperty('--panel-width', `${codePanel.width}px`);
    else element.style.removeProperty('--panel-width');
  }, [codePanel.width, layoutReady]);
  // D-266: the person's plan's file size where plans exist, otherwise the installation's
  const plan = usePlan();
  const planUploadMb = useUploadLimitMb();
  const maxUploadMb = plan ? planUploadMb : (meta.data?.uploadMaxMb ?? DEFAULT_UPLOAD_MB);
  // B2cDosyaBoyutu: the file picked over the size stays as a marked chip, with the words under the box
  const [oversize, setOversize] = useState<{ name: string; size: number } | null>(null);
  const [text, setText] = useState('');
  const [title, setTitle] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [rewind, setRewind] = useState<{ seq: number | null } | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const [side, setSide] = useState<SideAnswer[]>([]);
  const [help, setHelp] = useState(false);
  const [modelOpen, setModelOpen] = useState(false);
  const [recapOffer, setRecapOffer] = useState(true);
  // `/skill add <source>` and the conversation menu (D-13); "save this as a skill" (SA-054) starts from this conversation
  const [skillAdd, setSkillAdd] = useState<string | null>(null);
  const [saveAsSkill, setSaveAsSkill] = useState(false);
  const [tools, setTools] = useState(false);
  // D-79: the turns scroll in their own box and follow the newest line only while the reader is at the bottom
  const bottom = useFollowBottom(conversationId);
  const fileInput = useRef<HTMLInputElement>(null);
  const cards = useMemo(() => pendingApprovals(state), [state]);
  const shell = useShell();
  useCardNotifications(cards, TAB_TITLE);

  // a conversation lives in its own experience (decision 2026-10-27)
  const c = conversation.data;
  const panelOpen = c?.profile === 'code' || cited !== null;
  useEffect(() => {
    shell.setPanel(panelOpen);
    return () => shell.setPanel(false);
  }, [panelOpen, shell]);
  useEffect(() => {
    if (!c || c.profile === experience) return;
    const origin = homeOf(meta.data, c.profile);
    if (origin) window.location.replace(`${origin}/c/${c.id}`);
  }, [c, experience, meta.data]);
  // QW-203: a title, pin or model change made elsewhere shows here too
  useEffect(() => {
    if (state.metaVersion === 0) return;
    void queryClient.invalidateQueries({ queryKey: keys.conversation(conversationId) });
    void queryClient.invalidateQueries({ queryKey: keys.catalogueAll });
  }, [state.metaVersion, conversationId, queryClient]);
  // a new turn moves the conversation to the top of the list
  useEffect(() => {
    if (!state.running) void queryClient.invalidateQueries({ queryKey: keys.catalogueAll });
  }, [state.running, queryClient]);
  // the lists' status dots follow this conversation's run and cards at once (D-78)
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: keys.activity });
  }, [state.running, cards.length, queryClient]);
  // a card that waits for the user comes into view at the end of the conversation
  const cardCount = useRef(cards.length);
  useEffect(() => {
    if (cards.length > cardCount.current) bottom.follow();
    cardCount.current = cards.length;
  }, [cards.length, bottom]);

  // uploads go to the conversation at once and are read meanwhile; the chat's sandbox sees them read-only (SA-068);
  // picker, drag and drop, paste (QW-1001); the same content is not added twice (D-94); a chip taken out is deleted (D-93)
  const attachments = useComposerFiles({
    upload: async (file) => {
      const dto = await upload<ConversationFileDto>('POST', `/api/conversations/${conversationId}/files`, file, {
        'x-file-name': encodeURIComponent(file.name || `pasted-${Date.now()}.png`),
      });
      void queryClient.invalidateQueries({ queryKey: docKeys.conversation(conversationId) });
      return dto;
    },
    remove: async (file) => {
      await api('DELETE', `/api/conversations/${conversationId}/files/${file.id}`);
      void queryClient.invalidateQueries({ queryKey: docKeys.conversation(conversationId) });
    },
  });
  const send = useMutation({
    mutationFn: async (value: string) => {
      await attachments.settled();
      const attachmentIds = attachments.ids();
      return api<{ seq: number }>('POST', `/api/conversations/${conversationId}/messages`, { text: value, ...(attachmentIds.length > 0 ? { attachmentIds } : {}) });
    },
    onSuccess: () => {
      setText('');
      attachments.clear();
    },
  });
  const addFiles = (files: File[]) => {
    if (files.length === 0) return;
    const over = files.find((file) => file.size > maxUploadMb * 1024 * 1024);
    if (over) {
      setOversize({ name: over.name, size: over.size });
      return;
    }
    setOversize(null);
    attachments.add(files);
  };
  const rename = useMutation({
    mutationFn: (value: string) => api<ConversationDto>('PATCH', `/api/conversations/${conversationId}`, { title: value }),
    onSuccess: (data) => {
      queryClient.setQueryData(keys.conversation(conversationId), data);
      void queryClient.invalidateQueries({ queryKey: keys.catalogueAll });
      setTitle(null);
    },
  });
  // QW-105: stop the turn, its tools and its helpers
  const stop = useMutation({ mutationFn: (runId: string) => api('POST', `/api/runs/${runId}/cancel`) });
  // D-01, QW-208: continue after a limit or an interruption
  const proceed = useMutation({ mutationFn: () => api('POST', `/api/conversations/${conversationId}/continue`) });
  const compact = useMutation({ mutationFn: () => api('POST', `/api/conversations/${conversationId}/compact`) });
  const branch = useMutation({
    mutationFn: () => api<ConversationDto>('POST', `/api/conversations/${conversationId}/branch`, {}),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: keys.catalogueAll });
      void navigate(`/c/${created.id}`);
    },
  });
  const fresh = useMutation({
    mutationFn: () => api<ConversationDto>('POST', `/api/workspaces/${c?.workspaceId}/conversations`, {}),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: keys.catalogueAll });
      void navigate(`/c/${created.id}`);
    },
  });
  const recap = useMutation({ mutationFn: () => api<{ text: string }>('POST', `/api/conversations/${conversationId}/recap`) });
  // QW-613: thinking on or off for this conversation (in the "⋯" menu)
  const thinking = useMutation({
    mutationFn: (value: 'auto' | 'off') => api<ConversationDto>('PATCH', `/api/conversations/${conversationId}`, { thinking: value }),
    onSuccess: (data) => queryClient.setQueryData(keys.conversation(conversationId), data),
  });
  // the skills the user may run with /name here (QW-1205)
  // 03a2 §3: a copy of the conversation works on a directive in the background and reports back
  const fork = useMutation({ mutationFn: (directive: string) => api('POST', `/api/conversations/${conversationId}/fork`, { directive }) });
  const input = useRef<HTMLTextAreaElement>(null);
  // QW-108: a failed answer is asked for again with the same message
  const retry = useMutation({ mutationFn: () => api('POST', `/api/conversations/${conversationId}/regenerate`, {}) });
  // D-97: after a budget ran out, "Yeniden dene" continues the work where it stopped; the server checks the budget
  // first, and one that is still used up answers with its reset time, which the notice shows instead of an error
  const budgetRetry = useMutation({ mutationFn: () => api('POST', `/api/conversations/${conversationId}/continue`) });
  const budgetStill = budgetRetry.error instanceof ApiClientError && budgetRetry.error.code === 'quota_exceeded' ? (budgetRetry.error.quota ?? null) : null;
  const askSide = (question: string) => {
    const id = Date.now();
    setSide((current) => [...current, { id, question, answer: null, error: null }]);
    api<{ answer: string }>('POST', `/api/conversations/${conversationId}/side-question`, { text: question }).then(
      (result) => setSide((current) => current.map((entry) => (entry.id === id ? { ...entry, answer: result.answer } : entry))),
      (error: unknown) => setSide((current) => current.map((entry) => (entry.id === id ? { ...entry, error } : entry))),
    );
  };
  const exportAs = (format: string) => {
    window.location.assign(`/api/conversations/${conversationId}/export?format=${format === 'json' ? 'json' : 'md'}&locale=${currentLocale()}`);
  };

  // the "/" menu, shared with the home page's composer (QW-113, QW-1205)
  const slash = useSlashMenu({ text, setText, place: { workspaceId: conversation.data?.workspaceId, conversationId }, enabled: conversation.isSuccess });

  /** D-99: a click that needs the panel shows it (if hidden) on the right tab. */
  const openPanel = (request: { tab: 'files'; path: string } | { tab: 'preview'; port: number }) => {
    if (codePanel.hidden) codePanel.setHidden(false);
    setPanelRequest({ ...request, nonce: Date.now() });
  };
  const cite = (messageId: string, n: number) => {
    setCited({ messageId, n });
    // in a code workspace the source opens in the panel's place
    if (isCode && codePanel.hidden) codePanel.setHidden(false);
  };
  const fileNames = useMemo(() => (isCode ? fileResolver(knownFiles(state.items, topFolder.data)) : null), [isCode, state.items, topFolder.data]);

  // Phase 15e: the person's Microsoft account, read once the conversation used it; its address keeps them out of a
  // meeting's people on the cards
  const microsoft365 = useMicrosoft365Status(state.microsoftItems.length > 0);
  const accountEmail = microsoft365.data?.account?.email ?? null;
  const selfAddresses = useMemo(() => (accountEmail ? [accountEmail] : []), [accountEmail]);
  // Back from Microsoft's sign-in (?microsoft365=connected): the turn that stopped for want of the connection is
  // answered once, on its own ("Bağladıktan sonra Oppy sorunuzu kendiliğinden yanıtlar"). The notice asks for the
  // same when a code sign-in went through on this page or the person, connected by now, presses "Devam et".
  const [searchParams, setSearchParams] = useSearchParams();
  const backConnected = searchParams.getAll('microsoft365').includes('connected');
  const [resumeWanted, setResumeWanted] = useState<'return' | 'notice' | null>(null);
  const resumedFor = useRef<number | null>(null);
  const resumeAfterConnect = useMutation({
    mutationFn: () => api('POST', `/api/conversations/${conversationId}/continue`),
    // a continue that failed started no turn: the same notice may ask again ("Yeniden dene"); the guard stays while
    // the request runs and after it went through, so the return from Microsoft and "Devam et" never send two
    onError: () => {
      resumedFor.current = null;
    },
  });
  const continueAfterConnect = resumeAfterConnect.mutate;
  const followBottom = bottom.follow;
  useEffect(() => {
    if (!backConnected) return;
    setResumeWanted('return');
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('microsoft365');
        return next;
      },
      { replace: true },
    );
  }, [backConnected, setSearchParams]);
  // the replay must have reached the turn's end first, so the last notice is really the last thing
  const replayed = conversation.data ? state.lastSeq >= conversation.data.lastEventSeq : false;
  const lastItem = state.items.at(-1);
  const lastMicrosoft = lastItem?.kind === 'notice' && lastItem.code === 'failed' && isMicrosoft365TurnCode(lastItem.errorCode) ? lastItem : null;
  const lastMicrosoftSeq = lastMicrosoft?.seq ?? null;
  const lastMicrosoftCode = lastMicrosoft?.errorCode ?? null;
  useEffect(() => {
    if (!resumeWanted || !replayed || state.running) return;
    setResumeWanted(null);
    if (lastMicrosoftSeq === null || resumedFor.current === lastMicrosoftSeq) return;
    // coming back from Microsoft's page answers only a turn that waited for the account
    if (resumeWanted === 'return' && !CONNECT_CODES.has(lastMicrosoftCode ?? '')) return;
    resumedFor.current = lastMicrosoftSeq;
    continueAfterConnect();
    followBottom();
  }, [resumeWanted, replayed, state.running, lastMicrosoftSeq, lastMicrosoftCode, continueAfterConnect, followBottom]);
  const connectedHere = () => {
    setResumeWanted('notice');
    void queryClient.invalidateQueries({ queryKey: microsoft365Key });
  };

  // Microsoft 365 part 3 (MsEkleMenu, MsBelgeSec): the "+" group of a chat conversation and its picker; "Bu sohbet"
  // comes into the box like an upload, a library pick is linked to this conversation by the server
  const chatProfile = conversation.data?.profile !== 'code';
  const filesStatus = useMicrosoft365Status(conversation.isSuccess && chatProfile);
  const microsoftMenu = addMenuView(filesStatus.data, chatProfile ? 'chat' : 'code');
  const [picker, setPicker] = useState<{ source: Microsoft365Source; signInError: string | null } | null>(null);
  const [imported, setImported] = useState<ImportSummary | null>(null);
  // back from Microsoft's page with the picker's way back: it opens again on the same source
  const pickerBack = pickerSourceOf(searchParams);
  useEffect(() => {
    if (!pickerBack) return;
    setPicker({ source: pickerBack, signInError: searchParams.get('microsoft365') === 'error' ? searchParams.get('reason') : null });
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(PICKER_PARAM);
        // the page's own clean-up of "connected" runs in the same moment from the same address: drop it here too, or
        // this change would bring it back (an error stays for Microsoft365Result)
        if (!next.getAll('microsoft365').includes('error')) next.delete('microsoft365');
        return next;
      },
      { replace: true },
    );
  }, [pickerBack, searchParams, setSearchParams]);
  const addFromMicrosoft = (files: PickedFile[]) =>
    attachments.addSource(
      files.map((file) => ({
        name: file.name,
        size: file.size ?? 0,
        fetch: async () => {
          const dto = await addConversationFile(conversationId, file.item);
          void queryClient.invalidateQueries({ queryKey: docKeys.conversation(conversationId) });
          return dto;
        },
      })),
    );

  if (conversation.isPending) return <PageLoading label={t('common.loading')} />;
  if (conversation.isError || !c) {
    return (
      <div className="page">
        <ErrorAlert error={conversation.error} action={<Link to="/">{t('notFound.back')}</Link>} />
      </div>
    );
  }
  const drafts = Object.entries(state.drafts);
  const mode = state.mode ?? server.data?.approvalMode ?? c.approvalMode;
  const tasks = Object.values({ ...Object.fromEntries((server.data?.tasks ?? []).map((task) => [task.id, task])), ...state.tasks });
  const lastNotice = [...state.items].reverse().find((item) => item.kind === 'notice');
  const offerContinue = canContinue(state);
  const answer = lastAnswer(state);
  const points = rewindPoints(state);
  const idle = !state.running;
  // D-265: the last turn stopped on the daily allowance and waits for it (plans only)
  const waitingAllowance = Boolean(plan) && idle && lastNotice !== undefined && state.items.at(-1) === lastNotice && lastNotice.kind === 'notice' && lastNotice.code === 'failed' && lastNotice.errorCode === 'quota_exceeded';

  /** Slash commands run here (QW-113); anything else is a message. */
  const runCommand = (command: SlashCommand, argument: string) => {
    switch (command) {
      case 'compress':
        compact.mutate();
        break;
      case 'clear':
        fresh.mutate();
        break;
      case 'rewind':
        setRewind({ seq: null });
        break;
      case 'export':
        exportAs(argument);
        break;
      case 'btw':
        if (argument) askSide(argument);
        break;
      case 'model':
        setModelOpen(true);
        break;
      case 'memory':
        void navigate('/memory');
        break;
      case 'help':
        setHelp(true);
        break;
      case 'title':
        if (argument) rename.mutate(argument);
        else setTitle(c.title ?? '');
        break;
      case 'branch':
        branch.mutate();
        break;
      case 'fork':
        if (argument) fork.mutate(argument);
        break;
      case 'recap':
        recap.mutate();
        break;
      case 'context':
        openMenu('context-ring');
        break;
      case 'skill': {
        const source = skillAddSource(argument);
        if (source !== null) setSkillAdd(source);
        else void navigate('/skills');
        break;
      }
      default:
        break;
    }
  };
  const submit = () => {
    const value = text.trim();
    if (!value || attachments.busy) return;
    const slash = parseSlash(value);
    if (slash) {
      // a side question and a fork need their text
      if ((slash.command === 'btw' || slash.command === 'fork') && !slash.argument) return;
      runCommand(slash.command, slash.argument);
      setText('');
      return;
    }
    send.mutate(value);
    // a new message brings the reader back to the newest line
    bottom.follow();
  };

  /** The conversation's own lines that belong to no turn: messages, summaries, saved memories. */
  const renderLine = (item: ChatItem): ReactNode => {
    switch (item.kind) {
      case 'notice':
        return renderNotice(item);
      case 'notification':
        return (
          <p key={`b${item.seq}-${item.label}`} className="line-note" data-testid="task-notification">
            <Icons.done size={16} />
            {t('agent.notification', { label: item.label })}
          </p>
        );
      case 'compaction':
        return <CompactionLine key={`c${item.seq}`} item={item} />;
      case 'memory':
        return <MemoryLine key={`m${item.seq}`} item={item} />;
      case 'forked':
        return <ForkedLine key={`f${item.seq}`} item={item} />;
      case 'capabilities':
        return <CapabilitiesLine key={`cap${item.seq}`} item={item} />;
      case 'preview':
        // D-99: a server the agent started; the panel opens on its preview only when asked. v0.15a.3: where previews
        // are off the line says so and opens the files instead
        return c.profile === 'code' ? (
          <p key={`pv${item.seq}`} className="preview-line" data-testid="preview-line" data-port={item.port}>
            <Icons.monitor size={16} />
            <span>{t(previewsOff ? 'codePanel.previewOff' : 'codePanel.previewReady', { port: item.port })}</span>
            {previewsOff ? (
              <Button size="sm" variant="secondary" onClick={() => openPanel({ tab: 'files', path: '.' })} data-testid="preview-line-files">
                {t('codePanel.showFiles')}
              </Button>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => openPanel({ tab: 'preview', port: item.port })} data-testid="preview-line-open">
                {t('codePanel.openPreview')}
              </Button>
            )}
          </p>
        ) : null;
      case 'user': {
        if (editing === item.seq) return <EditMessage key={item.messageId} conversationId={c.id} item={item} onClose={() => setEditing(null)} />;
        const queued = state.running && !state.delivered.includes(item.seq);
        // the row stays while a turn runs (its buttons wait), so nothing below it moves (D-79)
        const rewindable = item.seq > state.compactedThrough;
        return (
          <div key={item.messageId} className="message message--user" data-testid="message-user" data-seq={item.seq}>
            <span className="ui-visually-hidden">{t('conversation.you')}</span>
            <AttachmentChips conversationId={c.id} attachments={item.attachments} documents={documents.data} />
            <div className="message__bubble">
              <p className="message__text">{item.text}</p>
            </div>
            {queued ? (
              <span className="message__queued">
                <Badge>{t('agent.queued')}</Badge>
              </span>
            ) : null}
            <UserMessageActions disabled={!idle} {...(rewindable ? { onEdit: () => setEditing(item.seq), onRewind: () => setRewind({ seq: item.seq }) } : {})} />
          </div>
        );
      }
      default:
        return null;
    }
  };

  function renderNotice(item: Extract<ChatItem, { kind: 'notice' }>): ReactNode {
    const latest = idle && item === lastNotice && state.items.at(-1) === item;
    // Phase 15e (MsDurumlar): a Microsoft 365 tool could not run; the card says why and what to do
    if (item.code === 'failed' && isMicrosoft365TurnCode(item.errorCode)) {
      return (
        <ErrorBoundary key={`n${item.seq}`} name="microsoft365-notice" resetKey={item.seq} fallback={() => <NoticeLine item={item} />}>
          <Microsoft365Notice code={item.errorCode} conversationId={c?.id ?? conversationId} latest={latest} onConnected={connectedHere} />
        </ErrorBoundary>
      );
    }
    // D-265: with plans the question waits for the daily allowance and goes on by itself (B2cTokenBitti, B2cGunlukBitti)
    if (item.code === 'failed' && item.errorCode === 'quota_exceeded' && plan) {
      return <AllowanceCard key={`n${item.seq}`} quota={item.quota ?? null} latest={latest} />;
    }
    if (item.code === 'failed' && item.errorCode === 'quota_exceeded') {
      return (
        <BudgetNotice
          key={`n${item.seq}`}
          quota={item.quota ?? null}
          still={latest ? budgetStill : null}
          profile={c?.profile === 'code' ? 'code' : 'chat'}
          errorCode={item.errorCode}
          {...(latest ? { onRetry: () => budgetRetry.mutate(), retrying: budgetRetry.isPending } : {})}
        />
      );
    }
    // 16b.1 (HataUzunDosya, HataBosCevap): a turn that got no answer says why and what to do; 16c.1: an empty reply too
    if (item.code === 'failed' && item.errorCode && NO_ANSWER_CODES.includes(item.errorCode)) {
      const run = item.runId ? state.runs[item.runId] : undefined;
      const durationMs = run?.startedAt && run.completedAt ? Math.max(0, Date.parse(run.completedAt) - Date.parse(run.startedAt)) : null;
      return (
        <FailureCard
          key={`n${item.seq}`}
          runId={item.runId}
          failure={item.errorCode === 'empty_reply' ? null : (item.failure ?? run?.failure ?? null)}
          empty={item.errorCode === 'empty_reply'}
          durationMs={shownDuration(durationMs)}
          model={(item.runId ? state.turnUsage[item.runId]?.model : undefined) ?? null}
          profile={c?.profile === 'code' ? 'code' : 'chat'}
          latest={latest}
          onRetry={() => retry.mutate()}
          retrying={retry.isPending}
          onSimple={(suggestion) => {
            setText(suggestion);
            input.current?.focus();
          }}
        />
      );
    }
    return (
      <NoticeLine
        key={`n${item.seq}`}
        item={item}
        {...(offerContinue && item === lastNotice ? { onContinue: () => proceed.mutate(), continuing: proceed.isPending } : {})}
        {...(latest && item.code === 'failed' ? { onRetry: () => retry.mutate(), retrying: retry.isPending } : {})}
      />
    );
  }

  /**
   * A turn (Phase 13b, docs/design/mockups/Adimlar.dc.html): the steps line
   * under the message, then what the turn wrote and made. A message with
   * only a thought makes no bubble; its thought is a step. In CompanyCode,
   * approval cards, changes and command output keep cards of their own.
   * The steps line always opens the turn and the streaming draft sits where
   * the answer will, so finishing the turn moves nothing (D-79).
   */
  const renderTurn = (block: Extract<Block, { kind: 'turn' }>): ReactNode => {
    const turn = turnSteps(block.runId, block.items, state);
    const texts = block.items.filter(hasText);
    const last = texts.at(-1);
    const live = state.running && state.activeRunId === block.runId;
    const usage = state.turnUsage[block.runId];
    const turnSources = readSources(block.items, state.sources.filter((source) => source.runId === block.runId));
    // skill loads and document searches are steps of the line, counted in its summary (Adimlar.dc.html); with plans a
    // question that only waits for the daily allowance has the card alone (D-265, B2cTokenBitti)
    const showSteps = live || (showsLine(turn, texts.length > 0) && !(plan && texts.length === 0 && waitsForAllowance(turn)));
    const head = showSteps ? (
      <div className="turn__head">
        <Steps
          turn={turn}
          profile={c.profile}
          allowance={Boolean(plan)}
          usage={
            usage ? (
              <span data-testid="turn-usage">
                {t('usage.turn', {
                  input: usage.inputTokens.toLocaleString(currentLocale()),
                  output: usage.outputTokens.toLocaleString(currentLocale()),
                  calls: usage.modelCalls,
                })}
              </span>
            ) : undefined
          }
        />
      </div>
    ) : null;
    const items = block.items.filter((item) => {
      switch (item.kind) {
        case 'assistant':
          return hasText(item);
        case 'tool':
          // the question and plan cards stand for these calls; a call waiting for approval is shown by its card
          if (item.tool === 'ask_user_question' || item.tool === 'exit_plan_mode') return false;
          if (cards.some((card) => card.callId === item.callId && card.runId === item.runId)) return false;
          // helpers keep their cards in both experiences (QW-809): their own progress, cost and result
          return item.tool === 'agent' || (c.profile === 'code' && CODE_CARDS.has(item.tool));
        case 'skill':
        case 'docwait':
          return false;
        case 'approval': {
          // QW-1307: a card waiting for the user stands above the composer; decided ones stay in the flow
          const approval = state.approvals[item.approvalId];
          return Boolean(approval && approval.status !== 'pending');
        }
        default:
          return true;
      }
    });
    const render = (item: ChatItem): ReactNode => {
      switch (item.kind) {
        case 'assistant': {
          const markdown = (source: string, key?: string) => (
            <Markdown
              key={key}
              source={source}
              className="message__markdown"
              citations={item.citations}
              onCite={(n) => cite(item.messageId, n)}
              files={fileNames ? { resolve: fileNames, open: (path) => openPanel({ tab: 'files', path }) } : null}
            />
          );
          // D-197: the text is cut where each Microsoft 365 card stands; a card that cannot be drawn leaves the rest
          return (
            <article key={item.messageId} className="answer" data-testid="message-assistant">
              <span className="ui-visually-hidden">{t('conversation.assistant')}</span>
              {item.cards.length === 0
                ? markdown(item.text)
                : answerParts(item.text, item.cards).map((part, n) =>
                    part.kind === 'text' ? (
                      markdown(part.text, `text${n}`)
                    ) : (
                      <ErrorBoundary key={`card${part.index}`} name="answer-card" resetKey={item.messageId} fallback={() => <AnswerCardError />}>
                        <AnswerCardView card={part.card} self={selfAddresses} conversationId={c.id} />
                      </ErrorBoundary>
                    ),
                  )}
            </article>
          );
        }
        case 'tool':
          return <ToolCard key={item.callId} item={item} profile={c.profile} output={state.toolOutput[item.callId]} />;
        case 'artifact':
          return <ArtifactCard key={`a${item.seq}`} item={item} />;
        case 'approval': {
          const approval = state.approvals[item.approvalId];
          return approval ? <ApprovalCard key={item.approvalId} approval={approval} profile={c.profile} /> : null;
        }
        case 'plan':
          return <ApprovedPlan key={`p${item.seq}`} item={item} />;
        case 'notice':
          return renderNotice(item);
        default:
          return renderLine(item);
      }
    };
    return (
      <div key={`t${block.runId}`} className="turn" data-testid="turn" data-run={block.runId} data-state={live ? 'running' : 'done'}>
        {head}
        {rows(items).map((row) => (row.kind === 'helpers' ? <HelperGroup key={`h${row.items[0]?.callId}`} items={row.items} /> : render(row.item)))}
        {live
          ? drafts.map(([messageId, draft]) => (
              <div key={messageId} className="answer answer--draft" data-testid="message-draft">
                {/* a card block being written is a placeholder until the answer comes with the card (D-197) */}
                <Markdown source={draft} className="message__markdown" cardsPending />
              </div>
            ))
          : null}
        {!live && (last || turnSources.length > 0) ? (
          <div className="turn__actions">
            {turnSources.length > 0 ? <SourcesPanel sources={turnSources} /> : null}
            {last ? (
              <AnswerActions
                conversation={c}
                // Copy takes the cards too, as plain text where they stand (D-197)
                item={last.cards.length > 0 ? { ...last, text: answerPlainText(last.text, last.cards, { t, locale: currentLocale(), now: new Date(), self: selfAddresses }) } : last}
                last={answer?.messageId === last.messageId}
                running={!idle}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const stale = Date.now() - Date.parse(c.lastActivityAt) > RECAP_AFTER_MS && state.items.filter((i) => i.kind === 'user').length >= 2;
  const conversationBlocks = blocks(state);
  const sendLabel = state.running ? t('conversation.queue') : t('conversation.send');

  // CompanyCode's composer has more controls: they go in a row under the text, the "+" first
  const stacked = c.profile === 'code';
  const addMenu = (
    <AddMenu
      label={t('home.add')}
      direction="up"
      experience={c.profile === 'code' ? 'code' : 'chat'}
      maxUploadMb={maxUploadMb}
      onUpload={() => fileInput.current?.click()}
      microsoft={microsoftMenu}
      onMicrosoft={(source) => setPicker({ source, signInError: null })}
      testId="composer-add"
      uploadTestId="composer-attach"
    >
      <AddMenuRow icon={<Icons.skills size={17} />} title={t('convTools.addSkill')} hint={t('microsoft365.files.menu.skillConversation')} onClick={() => setSkillAdd('')} />
      <AddMenuRow icon={<Icons.connections size={17} />} title={t('home.addConnection')} hint={t('microsoft365.files.menu.connection')} to="/connectors" />
    </AddMenu>
  );

  const main = (
    <div
      className={dragging ? 'conversation conversation--dragging' : 'conversation'}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (e.dataTransfer.files.length === 0) return;
        e.preventDefault();
        setDragging(false);
        addFiles([...e.dataTransfer.files]);
      }}
    >
      <header className="conversation-bar">
        <DrawerButton />
        <div className="conversation-bar__title">
          <Link to={`/w/${c.workspaceId}`} className="conversation-bar__crumb" data-testid="conversation-workspace">
            {workspace.data?.name ?? t('conversation.backToWorkspace')}
          </Link>
          {title !== null ? (
            <form
              className="conversation-bar__rename"
              onSubmit={(e) => {
                e.preventDefault();
                if (title.trim()) rename.mutate(title.trim());
              }}
            >
              <TextInput
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && setTitle(null)}
                aria-label={t('common.rename')}
                autoFocus
                data-testid="title-input"
              />
              <Button size="sm" type="submit" busy={rename.isPending}>
                {t('common.save')}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setTitle(null)}>
                {t('common.cancel')}
              </Button>
            </form>
          ) : (
            // the connection's chip sits in the title's line: the bar keeps its size when it comes (D-79)
            <div className="conversation-bar__heading-row">
              <h1 className="conversation-bar__heading">
                <span data-testid="conversation-title">{c.title ?? t('conversation.untitled')}</span>
              </h1>
              <Microsoft365ConversationChip used={state.microsoftItems.length > 0} />
            </div>
          )}
        </div>
        <div className="conversation-bar__controls">
          <LicenseChip />
          {c.profile === 'chat' ? <ModelMenu conversation={c} open={modelOpen} /> : null}
          <ContextRing conversationId={c.id} live={state.context} running={state.running} onCompact={() => compact.mutate()} onNewConversation={() => fresh.mutate()} />
          <ConversationDocumentsMenu conversationId={c.id} documents={documents.data ?? []} />
          {/* one menu for the rest (docs/design/mockups/SoruKarti.dc.html): thinking, skills, the conversation's tools, export */}
          <Menu
            align="right"
            label={t('conversation.options')}
            triggerClassName="ui-icon-button"
            trigger={<Icons.more size={18} />}
            panelClassName="conversation-menu__panel"
            data-testid="conversation-menu"
          >
            <label className="ui-menu-item conversation-menu__toggle" data-keep-open="" title={t('model.thinkingHint')}>
              <Icons.idea />
              <span className="conversation-menu__toggle-text">
                {t('model.thinking')}
                <span className="ui-menu-item__hint">{t('model.thinkingShort')}</span>
              </span>
              <Switch checked={c.thinking !== 'off'} disabled={thinking.isPending} onChange={(e) => thinking.mutate(e.target.checked ? 'auto' : 'off')} data-testid="thinking-toggle" />
            </label>
            <MenuItem icon={<Icons.skills />} onClick={() => setTools(true)} data-testid="menu-tools">
              {t('convTools.button')}
            </MenuItem>
            <MenuItem icon={<Icons.fileNew />} onClick={() => setSaveAsSkill(true)} data-testid="menu-save-as-skill">
              {t('convTools.saveAsSkill')}
            </MenuItem>
            <MenuSeparator />
            <MenuItem icon={<Icons.edit />} onClick={() => setTitle(c.title ?? '')} data-testid="title-rename">
              {t('common.rename')}
            </MenuItem>
            <MenuItem icon={<Icons.undo />} disabled={!idle || points.length === 0} onClick={() => setRewind({ seq: null })} data-testid="menu-rewind">
              {t('rewind.title')}
            </MenuItem>
            <MenuItem icon={<Icons.branch />} disabled={branch.isPending} onClick={() => branch.mutate()} data-testid="menu-branch">
              {t('branch.action')}
            </MenuItem>
            <MenuItem
              icon={<Icons.fork />}
              onClick={() => {
                setText('/fork ');
                input.current?.focus();
              }}
              data-testid="menu-fork"
            >
              {t('branch.fork')}
            </MenuItem>
            <MenuItem icon={<Icons.layers />} disabled={!idle || compact.isPending} onClick={() => compact.mutate()} data-testid="menu-compact">
              {t('context.compact')}
            </MenuItem>
            <MenuItem icon={<Icons.idea />} disabled={recap.isPending} onClick={() => recap.mutate()} data-testid="menu-recap">
              {t('recap.action')}
            </MenuItem>
            <MenuSeparator />
            <MenuItem icon={<Icons.download />} onClick={() => exportAs('md')} data-testid="menu-export-md">
              {t('export.markdown')}
            </MenuItem>
            <MenuItem icon={<Icons.download />} onClick={() => exportAs('json')} data-testid="menu-export-json">
              {t('export.json')}
            </MenuItem>
          </Menu>
          {otherOn || (c.profile === 'code' && workspace.data) ? <span className="conversation-bar__divider" aria-hidden="true" /> : null}
          <ExperienceSwitch experience={experience} compact />
          <PlanChip />
          <ProBadge />
          {c.profile === 'code' && workspace.data ? (
            <PanelToggle hidden={codePanel.hidden} running={sandboxActive || state.preview !== null} onToggle={() => codePanel.setHidden(!codePanel.hidden)} />
          ) : null}
        </div>
      </header>

      <div className="conversation__body">
        {/* over the turns, not in the bar: the bar never changes size (D-79) */}
        <span className={lost ? 'connection-pill' : 'ui-visually-hidden'} role="status">
          <span data-testid="connection-state" data-state={connection}>
            {connection === 'open' ? t('conversation.connected') : connection === 'connecting' ? t('conversation.connecting') : t('conversation.reconnecting')}
          </span>
        </span>
        {/* a region of its own: keyboard users can focus it and scroll the turns (WCAG 2.1.1) */}
        <div className="conversation__scroll" ref={bottom.scrollerRef} role="region" aria-label={t('conversation.turnsLabel')} tabIndex={0} data-testid="conversation-scroll">
          <div className="conversation__column" ref={bottom.contentRef}>
            {personalOpened === conversationId ? (
              <p className="line-note" role="status" data-testid="personal-workspace-note">
                <Icons.workspace size={16} />
                {t('conversation.personalWorkspace')}
              </p>
            ) : null}
            <ErrorAlert
              error={rename.error ?? proceed.error ?? stop.error ?? compact.error ?? branch.error ?? fresh.error ?? retry.error ?? fork.error ?? thinking.error ?? resumeAfterConnect.error ?? (budgetStill ? null : budgetRetry.error)}
            />
            {stale && recapOffer && !recap.data && !recap.isPending ? (
              <div className="recap-offer" data-testid="recap-offer">
                <Icons.idea size={18} />
                <span>{t('recap.offer')}</span>
                <Button size="sm" variant="secondary" onClick={() => recap.mutate()}>
                  {t('recap.action')}
                </Button>
                <IconButton size="sm" label={t('common.close')} icon={<Icons.close size={16} />} onClick={() => setRecapOffer(false)} />
              </div>
            ) : null}
            <RecapPanel text={recap.data?.text ?? null} error={recap.error} pending={recap.isPending} onClose={() => recap.reset()} />

            <div className="messages" aria-busy={state.running} data-testid="messages">
              {state.items.length === 0 && drafts.length === 0 && !state.running ? (
                <div className="conversation__empty">
                  <Icons.conversations size={22} />
                  <p>{t(c.profile === 'code' ? 'conversation.emptyCode' : 'conversation.empty')}</p>
                </div>
              ) : null}
              {conversationBlocks.map((block) => (block.kind === 'turn' ? renderTurn(block) : renderLine(block.item)))}
              {/* Phase 15e: back from Microsoft's page without the account: why, in plain words, next to the notice */}
              {searchParams.get('microsoft365') === 'error' ? <Microsoft365Result /> : null}
              {/* QW-1307: what waits for the user is the newest thing, right above the composer, never under it */}
              {cards.length > 0 ? (
                <div className="waiting-cards" data-testid="waiting-cards">
                  {cards.map((card) => (
                    <ApprovalCard key={card.id} approval={card} profile={c.profile} />
                  ))}
                </div>
              ) : null}
              {/* a turn's end is announced once; the steps line announces its steps */}
              <span className="ui-visually-hidden" role="status">
                {!state.running && answer ? t('conversation.answerReady') : ''}
              </span>
            </div>
          </div>
        </div>
        {bottom.away ? (
          <button
            type="button"
            className="to-bottom"
            onClick={() => {
              bottom.follow();
              input.current?.focus();
            }}
            data-testid="to-bottom"
          >
            <Icons.arrowDown size={16} />
            {t('conversation.toBottom')}
          </button>
        ) : null}
      </div>

      <form
        className="composer"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="composer__stack">
          <TodoPanel todos={state.todos} />
          <TasksPanel conversationId={c.id} tasks={tasks} />
          {help ? <SlashHelp onClose={() => setHelp(false)} /> : null}
          <SideQuestions entries={side} onClose={() => setSide([])} />
          <ErrorAlert error={send.error ?? attachments.error} />
          {imported ? <ImportNotice summary={imported} linked="conversation" onClose={() => setImported(null)} /> : null}
          <SlashMenu entries={slash.entries} active={slash.active} onPick={slash.pick} />
        </div>
        {readOnly ? (
          <ReadOnlyCard
            onExport={() => exportAs('md')}
            extra={
              state.running && state.activeRunId ? (
                <Button variant="secondary" icon={<Icons.stop size={14} fill="currentColor" />} busy={stop.isPending} onClick={() => stop.mutate(state.activeRunId as string)} data-testid="composer-stop">
                  {t('agent.stop')}
                </Button>
              ) : null
            }
          />
        ) : (
          <>
          <div className={stacked ? 'composer-box composer-box--chat composer-box--stacked' : 'composer-box composer-box--chat'}>
            {stacked ? null : addMenu}
            <input
              ref={fileInput}
              type="file"
              multiple
              className="ui-visually-hidden"
              tabIndex={-1}
              data-testid="composer-file"
              aria-label={t('conversation.attach')}
              onChange={(e) => {
                const files = [...(e.target.files ?? [])];
                e.target.value = '';
                addFiles(files);
              }}
            />
            <div className="composer-box__main">
              {oversize ? (
                <ul className="chips composer-files">
                  <OversizeChip name={oversize.name} size={oversize.size} onRemove={() => setOversize(null)} />
                </ul>
              ) : null}
              <ComposerFiles entries={attachments.entries} onRemove={attachments.remove} />
              <textarea
                ref={input}
                className="composer-box__input"
                rows={1}
                aria-label={t('conversation.placeholder')}
                placeholder={t(
                  waitingAllowance
                    ? allowancePlaceholder(plan)
                    : cards.some((card) => card.kind === 'question')
                    ? 'conversation.placeholderQuestion'
                    : // part 3 (MsSohbetOnay): "Oppy onayınızı bekliyor" while a Microsoft 365 write card waits; "Durdur" stays
                      cards.some((card) => card.microsoft365)
                      ? 'agent.notify.approval'
                      : c.profile === 'code'
                        ? 'home.placeholderCode'
                        : 'conversation.placeholder',
                )}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onPaste={(e) => {
                  const files = [...e.clipboardData.files];
                  if (files.length > 0) {
                    e.preventDefault();
                    addFiles(files);
                  }
                }}
                onKeyDown={(e) => {
                  if (slash.onKeyDown(e)) return;
                  if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                    e.preventDefault();
                    submit();
                  }
                }}
                data-testid="composer-input"
              />
            </div>
            <div className="composer-box__tools">
              {stacked ? addMenu : null}
              {c.profile === 'code' && server.data ? <ModeSelector conversationId={c.id} mode={mode} available={server.data.availableModes} compact /> : null}
              {c.profile === 'code' ? <ModelPicker conversation={c} open={modelOpen} /> : null}
              <span className="composer-box__spacer" />
              {/* D-17: a side question only when the user asks for one */}
              <Button
                size="sm"
                variant="secondary"
                disabled={!text.trim() || text.trim().startsWith('/')}
                onClick={() => {
                  askSide(text.trim());
                  setText('');
                }}
                data-testid="composer-side"
                title={t('side.hint')}
              >
                {t('side.button')}
              </Button>
              {state.running && state.activeRunId ? (
                <IconButton
                  size="lg"
                  variant={text.trim() ? 'outlined' : 'primary'}
                  label={t('agent.stop')}
                  icon={<Icons.stop size={14} fill="currentColor" />}
                  busy={stop.isPending}
                  onClick={() => stop.mutate(state.activeRunId as string)}
                  data-testid="composer-stop"
                />
              ) : null}
              {!state.running || text.trim() ? (
                <button
                  type="submit"
                  className="ui-icon-button ui-icon-button--primary ui-icon-button--lg"
                  aria-label={sendLabel}
                  title={sendLabel}
                  disabled={!text.trim() || attachments.busy || send.isPending}
                  data-testid="composer-send"
                >
                  {send.isPending ? <span className="ui-spinner ui-spinner--inline" aria-hidden="true" /> : <Icons.send size={20} />}
                </button>
              ) : null}
            </div>
          </div>
          {oversize ? <OversizeNotice size={oversize.size} limitMb={maxUploadMb} /> : null}
          <p className="composer__hint">{dragging ? t('conversation.dropHere') : t('conversation.attachmentLimit', { mb: maxUploadMb })}</p>
          </>
        )}
      </form>
      {skillAdd !== null ? <SkillAddDialog initialSource={skillAdd} workspaceId={c.workspaceId} onClose={() => setSkillAdd(null)} /> : null}
      {saveAsSkill ? <SkillAddDialog conversationId={c.id} workspaceId={c.workspaceId} onClose={() => setSaveAsSkill(false)} /> : null}
      {picker ? (
        <FilePicker
          source={picker.source}
          composer
          conversationId={c.id}
          returnPath={`/c/${c.id}`}
          signInError={picker.signInError}
          onClose={() => {
            setPicker(null);
            input.current?.focus();
          }}
          onAddToConversation={addFromMicrosoft}
          onImported={(summary) => {
            setImported(summary);
            void queryClient.invalidateQueries({ queryKey: docKeys.conversation(conversationId) });
          }}
        />
      ) : null}
      {tools ? (
        <ConversationToolsDialog
          conversation={c}
          onClose={() => setTools(false)}
          onAddSkill={() => {
            setTools(false);
            setSkillAdd('');
          }}
          onSaveAsSkill={() => {
            setTools(false);
            setSaveAsSkill(true);
          }}
        />
      ) : null}
      {rewind ? (
        <RewindDialog
          conversation={c}
          points={points}
          initialSeq={rewind.seq}
          onClose={() => setRewind(null)}
          onDone={(result) => {
            setRewind(null);
            // the removed message comes back to the composer
            if (result.text) setText(result.text);
            void queryClient.invalidateQueries({ queryKey: keys.context(conversationId) });
          }}
        />
      ) : null}
    </div>
  );

  // DOC-004: a citation opens its source next to the conversation (in a code workspace, in place of the environment)
  const citedAnswer = cited ? state.items.find((item): item is AssistantItem => item.kind === 'assistant' && item.messageId === cited.messageId) : undefined;
  const sources =
    cited && citedAnswer && citedAnswer.citations.length > 0 ? (
      <div className="conversation-layout__side">
        <ErrorBoundary name="citation-panel" resetKey={citedAnswer.messageId} fallback={(retry) => <PanelError retry={retry} />}>
          <CitationPanel citations={citedAnswer.citations} selected={cited.n} onSelect={(n) => cite(citedAnswer.messageId, n)} onClose={() => setCited(null)} />
        </ErrorBoundary>
      </div>
    ) : null;
  if (c.profile !== 'code' || !workspace.data) {
    return <div className={sources ? 'conversation-layout conversation-layout--panel' : 'conversation-layout'}>{main}{sources}</div>;
  }
  // code workspaces: the conversation next to its environment (files, terminal, git, preview), which the user can hide
  // and resize (KodPanel.dc.html, KodPanelKapali.dc.html; D-99); hidden, it keeps running out of sight
  return (
    <div
      ref={layout}
      className={`conversation-layout conversation-layout--code ${codePanel.hidden ? 'conversation-layout--hidden' : 'conversation-layout--panel'}`}
      data-testid="conversation-layout"
      data-panel={codePanel.hidden ? 'hidden' : 'shown'}
    >
      {main}
      <PanelDivider layout={layout} width={codePanel.width} onChange={codePanel.setWidth} />
      {sources ?? (
        <aside className="conversation-layout__side" aria-label={t('sandbox.panel')} data-testid="code-panel">
          <ErrorBoundary name="code-panel" resetKey={workspace.data.id} fallback={(retry) => <PanelError retry={retry} />}>
            <SandboxPanel workspace={workspace.data} preview={state.preview} filesVersion={state.filesVersion} request={panelRequest} />
          </ErrorBoundary>
        </aside>
      )}
    </div>
  );
}
